import { useState, useEffect } from "react";
import {
  User,
  PlusCircle,
  X,
  Loader2,
  CheckCircle2,
  AlertTriangle,
} from "lucide-react";
import { v4 as uuid } from "uuid";
import Backend from "../../../../Backend/Backend";
import { isMobile } from "react-device-detect";
import useDeviceDetection from "../../../../Utils/DeviceDetection/DeviceDetection";
import { useNavigate } from "react-router-dom";

import "./AccountSwitcher.css";

export default function AccountSwitcher({ onClose }) {
  const navigate = useNavigate();

  const [savedAccounts, setSavedAccounts] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [newAccount, setNewAccount] = useState({ username: "", password: "" });
  const [loading, setLoading] = useState(false);
  const [backend, setBackend] = useState(null);
  const [ip, setIp] = useState("");
  const [alert, setAlert] = useState({ type: "", message: "" });
  const currentUsername = localStorage.getItem("username");

  const device = useDeviceDetection();

  useEffect(() => {
    (async () => {
      const b = await new Backend().init();
      setBackend(b);
      const list = JSON.parse(localStorage.getItem("accountsList") || "[]");
      setSavedAccounts(list);
      getIp();
    })();
  }, []);

  const showAlert = (type, message) => {
    setAlert({ type, message });
    setTimeout(() => setAlert({ type: "", message: "" }), 3000);
  };

  const handleSwitch = (acc) => {
    try {
      isLogin(acc);
    } catch (err) {
      console.error("Error switching account:", err);
      showAlert("error", "Failed to switch account");
    }
  };

  const handleRemove = (acc) => {
    const filtered = savedAccounts.filter((a) => a.username !== acc.username);
    localStorage.setItem("accountsList", JSON.stringify(filtered));
    setSavedAccounts(filtered);
    showAlert("success", `Removed ${acc.username}`);
  };

  const handleSaveNewAccount = async () => {
    const { username, password } = newAccount;
    if (!username.trim() || !password.trim()) {
      showAlert("error", "Enter both username and password");
      return;
    }
    if (!backend) {
      showAlert("error", "Backend not initialized yet");
      return;
    }
    setLoading(true);

    try {
      const data = {
        server: localStorage.getItem("server"),
        id: localStorage.getItem("id"),
        username: localStorage.getItem("username"),
        token: localStorage.getItem("token"),
        cserver: localStorage.getItem("server"),
        cusername: username,
        cpassword: password,
      };

      const response = await backend.check_user(data);

      if (response?.error === "False") {
        const user_type = Number(response.type);
        const list = JSON.parse(localStorage.getItem("accountsList") || "[]");
        if (list.find((a) => a.username === response.username)) {
          showAlert("error", "Account already exists");
          setLoading(false);
          return;
        }

        const updated = [
          ...list,
          {
            server: response.server,
            username,
            password,
            user_type,
            logo: response.logo,
          },
        ];
        localStorage.setItem("accountsList", JSON.stringify(updated));
        setSavedAccounts(updated);
        setNewAccount({ username: "", password: "" });
        setShowModal(false);
        showAlert("success", "Account added successfully!");
      } else {
        showAlert("error", response?.message || "Failed to verify account");
      }
    } catch (error) {
      console.error(error);
      showAlert("error", "Network or server error");
    } finally {
      setLoading(false);
    }
  };

  const AlertBox = () =>
    alert.message && (
      <div
        className={`fixed top-5 right-5 z-[9999] flex items-center gap-3 p-4 rounded-xl text-sm font-medium shadow-xl backdrop-blur-md ${
          alert.type === "success"
            ? "bg-green-100/90 text-green-800 border border-green-300"
            : "bg-red-100/90 text-red-800 border border-red-300"
        }`}
      >
        {alert.type === "success" ? (
          <CheckCircle2 size={18} />
        ) : (
          <AlertTriangle size={18} />
        )}
        <span>{alert.message}</span>
      </div>
    );

  const handleLogin = async (loginData) => {
    try {
      setLoading(true);
      const r = await backend.login(loginData);
      setLoading(false);

      if (r.error === "False") {
        saveLoginData(r);
        showAlert("success", r.message);
        window.location.reload();
        navigate("/");
      } else {
        showAlert("error", r.message);
      }
    } catch (error) {
      setLoading(false);
      console.error("Login error:", error);
      showAlert("error", "Something went wrong!");
    }
  };

  // 🔹 Save to localStorage
  const saveLoginData = (r) => {
    localStorage.setItem("id", r.id);
    localStorage.setItem("server", r.server);
    localStorage.setItem("username", r.username);
    localStorage.setItem("token", r.token);
    localStorage.setItem("user_type", r.type);
    localStorage.setItem("IsLoggedIn", "1");

    // accountsList add this account to accountsList

    const list = JSON.parse(localStorage.getItem("accountsList") || "[]");
    const exists = list.find((a) => a.username === r.username);

    if (!exists) {
      list.push({
        server: r.server,
        username: r.username,
        password: newAccount.password, // optional (store only if needed)
        user_type: r.type,
        logo: r.logo || "",
      });
      localStorage.setItem("accountsList", JSON.stringify(list));
    }

    localStorage.setItem(
      "QuickQty",
      JSON.stringify([
        { id: "1", qty: "1" },
        { id: "2", qty: "2" },
        { id: "3", qty: "5" },
        { id: "4", qty: "10" },
        { id: "5", qty: "20" },
        { id: "6", qty: "30" },
        { id: "7", qty: "40" },
        { id: "8", qty: "50" },
      ])
    );
  };

  // 🔹 Normal login
  const isLogin = async (acc) => {
    if (!backend) return showAlert("error", "Backend not initialized yet!");
    if (!acc.server) return showAlert("error", "Server Not Found!");
    if (!acc.username) return showAlert("error", "Username Not Found!");
    if (!acc.password) return showAlert("error", "Password Not Found!");

    const data = {
      server: acc.server,
      username: acc.username,
      password: acc.password,
      device,
      devicename: isMobile ? "Mobile" : "WebBrowser",
      devicetype: isMobile ? "Mobile" : "WebBrowser",
      deviceid: uuid(),
      deviceip: ip,
    };

    await handleLogin(data);
  };

  const getIp = async () => {
    try {
      const response = await fetch("https://geolocation-db.com/json/");
      const data = await response.json();
      setIp(data.IPv4);
    } catch (error) {
      console.error("Error fetching IP:", error);
    }
  };

  return (
    <div className="max-w-4xl mx-auto bg-gradient-to-br from-[var(--background)] to-[var(--hover)] shadow-2xl p-6 rounded-3xl border border-[var(--hover)]">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-semibold text-[var(--text)] flex items-center gap-2">
          <User size={22} />
          Switch Account
        </h2>
        <button
          onClick={onClose}
          className="text-gray-400 hover:text-red-500 transition"
        >
          <X size={22} />
        </button>
      </div>

      {alert.message && <AlertBox />}

      {/* Account List */}
      <div className="mt-4 max-h-[65vh] overflow-y-auto space-y-4 pr-2 scrollbar-thin scrollbar-thumb-gray-600/50 scrollbar-track-transparent">
        {savedAccounts.length === 0 ? (
          <div className="text-center py-16 text-gray-400 text-lg">
            No saved accounts yet.
          </div>
        ) : (
          savedAccounts.map((acc) => (
            <div
              key={acc.username}
              className="group gap-10 flex items-center justify-between border border-[var(--hover)] bg-[var(--background)] p-4 rounded-2xl hover:border-[var(--primary)] hover:shadow-lg transition-all duration-200"
            >
              <div>
                <div className="text-lg font-semibold text-[var(--text)]">
                  {acc.username}
                </div>
                <div className="text-sm text-gray-400">
                  {acc.user_type === 1
                    ? "ADMIN"
                    : acc.user_type === 2
                    ? "SUPER"
                    : acc.user_type === 3
                    ? "MASTER"
                    : acc.user_type === 4
                    ? "USER"
                    : acc.user_type === 5
                    ? "BROKER"
                    : acc.user_type === 6
                    ? "SUBBROKER"
                    : "UNKNOWN"}
                </div>
              </div>
              <div className="flex gap-3">
                {currentUsername === acc.username ? (
                  <span className="px-4 py-1.5 rounded-lg text-green-600 bg-green-100 font-semibold">
                    Active
                  </span>
                ) : (
                  <button
                    onClick={() => handleSwitch(acc)}
                    className="px-4 py-1.5 rounded-lg bg-[var(--primary)] hover:bg-[var(--primary)] text-white font-medium transition"
                  >
                    Switch
                  </button>
                )}
                {currentUsername === acc.username ? (
                  <button
                    disabled
                    className="px-4 py-1.5 rounded-lg bg-[var(--sell)] text-white font-medium opacity-20 cursor-not-allowed transition"
                  >
                    Remove
                  </button>
                ) : (
                  <button
                    onClick={() => handleRemove(acc)}
                    className="px-4 py-1.5 rounded-lg bg-[var(--sell)] hover:bg-[var(--sell)] text-white font-medium transition"
                  >
                    Remove
                  </button>
                )}
              </div>
            </div>
          ))
        )}
      </div>

      {/* Add Account */}
      <div className="flex justify-center mt-8">
        <button
          onClick={() => setShowModal(true)}
          className="flex items-center gap-2 bg-[var(--primary)] hover:bg-[var(--primary)] text-white px-6 py-2.5 rounded-xl font-medium shadow-lg transition-all duration-200 hover:scale-[1.02]"
        >
          <PlusCircle size={18} />
          Add New Account
        </button>
      </div>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 z-[55] flex items-center justify-center bg-black/60 backdrop-blur-sm transition-all duration-300">
          <div className="relative bg-[var(--background)] dark:bg-[var(--background)] w-[95%] max-w-md rounded-2xl shadow-2xl border border-[var(--hover)] p-6 animate-fadeIn">
            {/* Header */}
            <div className="flex justify-between items-center mb-5 border-b border-[var(--hover)] pb-3">
              <h3 className="text-xl font-bold text-[var(--text)] flex items-center gap-2">
                <span className="inline-block w-2 h-5 bg-[var(--primary)] rounded-sm"></span>
                Add New Account
              </h3>
              <button
                onClick={() => setShowModal(false)}
                className="p-2 rounded-full hover:bg-[var(--text)] dark:hover:bg-[var(--text)] transition"
              >
                <X size={20} className="text-gray-500 dark:text-gray-400" />
              </button>
            </div>

            {/* Input Fields */}
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-[var(--text)] mb-1">
                  Username
                </label>
                <input
                  type="text"
                  placeholder="Enter username"
                  className="w-full p-3 rounded-lg border border-[var(--hover)] bg-transparent focus:ring-2 focus:ring-[var(--primary)] focus:border-[var(--primary)] text-[var(--text)] transition"
                  value={newAccount.username}
                  onKeyDown={(e) => e.key === "Enter" && handleSaveNewAccount()}
                  onChange={(e) =>
                    setNewAccount({ ...newAccount, username: e.target.value })
                  }
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-[var(--text)] mb-1">
                  Password
                </label>
                <input
                  type="password"
                  placeholder="Enter password"
                  className="w-full p-3 rounded-lg border border-[var(--hover)] bg-transparent focus:ring-2 focus:ring-[var(--primary)] focus:border-[var(--primary)] text-[var(--text)] transition"
                  value={newAccount.password}
                  onKeyDown={(e) => e.key === "Enter" && handleSaveNewAccount()}
                  onChange={(e) =>
                    setNewAccount({ ...newAccount, password: e.target.value })
                  }
                />
              </div>
            </div>

            {/* Button */}
            <button
              onClick={handleSaveNewAccount}
              disabled={loading}
              className={`w-full mt-6 py-3 rounded-lg font-semibold flex items-center justify-center gap-2 transition
          ${
            loading
              ? "bg-gray-400 cursor-not-allowed"
              : "bg-[var(--primary)] text-white shadow-md"
          }`}
            >
              {loading ? (
                <>
                  <Loader2 size={18} className="animate-spin" /> Adding...
                </>
              ) : (
                "Add Account"
              )}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
